import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import StatCard, { STAT_CHIP } from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { Users, LayoutGrid, PlayCircle, Calendar, Compass } from 'lucide-react';
import { clientService } from '../../services';
import { usePreferences } from '../../contexts/PreferencesContext';

/**
 * Client Dashboard - Minimalist Design
 * Clean, white-dominant design with line icons
 * Following "less is more" philosophy
 */

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { formatDateWithTimezone } = usePreferences();
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [inactiveTrainerCount, setInactiveTrainerCount] = useState(0);
  const [stats, setStats] = useState({
    totalTrainers: 0,
    totalSessions: 0,
    activeSessions: 0,
    scheduledSessions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  // Getting-started checklist: dismissal + "viewed report" action are persisted
  // in localStorage so they survive reloads (the other steps auto-derive from data).
  const [gsDismissed, setGsDismissed] = useState(() => localStorage.getItem('client_gs_dismissed') === '1');
  const [viewedReport, setViewedReport] = useState(() => localStorage.getItem('client_gs_viewed_report') === '1');

  // Onboarding steps for clients
  const onboardingSteps = [
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
        </svg>
      ),
      title: 'Invite & Manage Trainers',
      description: 'Add trainers to your organization, assign them to sessions, and manage their access permissions.',
      color: 'bg-purple-500',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2zm-7 5h5v5h-5v-5z"/>
        </svg>
      ),
      title: 'Create Training Sessions',
      description: 'Schedule new training sessions, set dates, times, and maximum participants. Define session objectives and customize settings.',
      color: 'bg-blue-500',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M21 6h-7.59l3.29-3.29L16 2l-4 4-4-4-.71.71L10.59 6H3c-1.1 0-2 .89-2 2v12c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.11-.9-2-2-2zm0 14H3V8h18v12zM9 10v8l7-4-7-4z"/>
        </svg>
      ),
      title: 'Monitor Live Sessions',
      description: 'Track real-time participant engagement, view activity responses, and oversee ongoing training sessions.',
      color: 'bg-green-500',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
        </svg>
      ),
      title: 'Review Reports & Analytics',
      description: 'Access detailed session reports, participant feedback, trainer performance metrics, and export data for analysis.',
      color: 'bg-red-500',
    },
  ];

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);

      // Show onboarding only on the user's very first login (tracked in DB)
      if (parsedUser.isFirstLogin) {
        setShowOnboarding(true);
      }
    }

    fetchDashboardData();
  }, []);

  // Handle onboarding completion
  const handleCompleteOnboarding = (redirectToHelp = false) => {
    // Clear isFirstLogin flag from stored user data so it doesn't show again
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      parsedUser.isFirstLogin = false;
      localStorage.setItem('user', JSON.stringify(parsedUser));
    }
    setShowOnboarding(false);
    setCurrentStep(0);

    // Redirect to help page if completing the full tour
    if (redirectToHelp) {
      navigate('/client/help');
    }
  };

  // Handle next step
  const handleNextStep = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Last step - redirect to help page
      handleCompleteOnboarding(true);
    }
  };

  // Handle previous step
  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Re-open the welcome tour on demand (the "Show me around" button) — works
  // even after the first-login auto-show has been dismissed.
  const handleReplayTour = () => {
    setCurrentStep(0);
    setShowOnboarding(true);
  };

  // Permanently hide the getting-started checklist for this browser.
  const dismissGettingStarted = () => {
    localStorage.setItem('client_gs_dismissed', '1');
    setGsDismissed(true);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await clientService.getDashboardStats();
      if (response.success) {
        const { stats: apiStats, recentSessions } = response.data;
        setStats({
          totalTrainers: apiStats.trainerCount || 0,
          totalSessions: apiStats.totalSessions || 0,
          activeSessions: apiStats.activeSessions || 0,
          scheduledSessions: apiStats.scheduledSessions || 0,
        });
        setSessions(recentSessions || []);
      }

      // Check for inactive trainers
      try {
        const trainersResponse = await clientService.getTrainers({ status: 'all', limit: 100 });
        if (trainersResponse.success) {
          const inactive = (trainersResponse.data.trainers || []).filter(t => t.trainerStatus === 'inactive').length;
          setInactiveTrainerCount(inactive);
        }
      } catch (e) {
        // Non-critical — don't block dashboard
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statsCards = [
    { title: 'Total Trainers',    value: stats.totalTrainers,     icon: Users,      variant: 'violet' },
    { title: 'Total Sessions',    value: stats.totalSessions,     icon: LayoutGrid, variant: 'blue' },
    { title: 'Active Sessions',   value: stats.activeSessions,    icon: PlayCircle, variant: 'emerald' },
    { title: 'Upcoming Sessions', value: stats.scheduledSessions, icon: Calendar,   variant: 'amber' },
  ];

  // Quick actions
  const quickActions = [
    {
      title: 'Create New Session',
      description: 'Schedule a new training session',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      ),
      action: () => navigate('/client/sessions/new'),
      variant: 'blue',
    },
    {
      title: 'Add Trainer',
      description: 'Add a new trainer to your team',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
        </svg>
      ),
      action: () => navigate('/client/trainers/add'),
      variant: 'purple',
    },
    {
      title: 'View Reports',
      description: 'Check session analytics',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      action: () => navigate('/client/reports'),
      variant: 'green',
    },
  ];

  // ── Getting-started checklist ──────────────────────────────────────────
  // Steps auto-check from live dashboard data; "Review a report" is satisfied
  // either by having a completed session or by the user opening reports once.
  const hasTrainer = stats.totalTrainers > 0;
  const hasSession = stats.totalSessions > 0;
  const hasReviewedReport = viewedReport || sessions.some((s) => s.status === 'completed');

  const gettingStartedSteps = [
    { key: 'trainer', label: 'Add your first trainer', done: hasTrainer, cta: 'Add trainer', action: () => navigate('/client/trainers/add') },
    { key: 'session', label: 'Create your first session', done: hasSession, cta: 'Create', action: () => navigate('/client/sessions/new') },
    {
      key: 'report',
      label: 'Review a session report',
      done: hasReviewedReport,
      cta: 'View reports',
      action: () => {
        localStorage.setItem('client_gs_viewed_report', '1');
        setViewedReport(true);
        navigate('/client/reports');
      },
    },
  ];
  const gsCompleted = gettingStartedSteps.filter((s) => s.done).length;
  const gsAllDone = gsCompleted === gettingStartedSteps.length;

  // Once every step is complete, show the "You're all set!" state briefly, then
  // auto-hide it permanently so it doesn't linger forever.
  useEffect(() => {
    if (loading || gsDismissed || !gsAllDone) return;
    const timer = setTimeout(() => {
      localStorage.setItem('client_gs_dismissed', '1');
      setGsDismissed(true);
    }, 3500);
    return () => clearTimeout(timer);
  }, [loading, gsDismissed, gsAllDone]);

  return (
    <DashboardLayout title="Dashboard">
      {/* Onboarding Tutorial Overlay */}
      {showOnboarding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Blurred Background - dashboard visible behind */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-md"
            onClick={handleCompleteOnboarding}
          />

          {/* Onboarding Card - Enhanced Modern Design */}
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative z-10 w-full max-w-2xl"
          >
            <div className="bg-white rounded-2xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)] overflow-hidden">
              {/* Main Content Area */}
              <div className="flex flex-col md:flex-row min-h-[400px]">
                {/* Left Side - Enhanced Gradient Visual */}
                <div className={`md:w-[40%] p-6 flex flex-col items-center justify-center relative overflow-hidden ${
                  currentStep === 0 ? 'bg-gradient-to-br from-purple-500 via-purple-600 to-violet-700' :
                  currentStep === 1 ? 'bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700' :
                  currentStep === 2 ? 'bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700' :
                  'bg-gradient-to-br from-rose-500 via-rose-600 to-pink-700'
                }`}>
                  {/* Animated floating shapes */}
                  <motion.div
                    animate={{ y: [0, -8, 0], rotate: [0, 5, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-6 right-6 w-12 h-12 bg-white/10 rounded-xl"
                  />
                  <motion.div
                    animate={{ y: [0, 8, 0], rotate: [0, -5, 0] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                    className="absolute top-16 left-3 w-8 h-8 bg-white/10 rounded-full"
                  />
                  <motion.div
                    animate={{ scale: [1, 1.1, 1], opacity: [0.1, 0.2, 0.1] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/10 rounded-full"
                  />

                  {/* Step Number */}
                  <motion.div
                    key={`step-num-${currentStep}`}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-4 left-4 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center"
                  >
                    <span className="text-white font-bold text-sm">{currentStep + 1}</span>
                  </motion.div>

                  {/* Icon with enhanced animation */}
                  <motion.div
                    key={`icon-${currentStep}`}
                    initial={{ scale: 0, rotate: -180, opacity: 0 }}
                    animate={{ scale: 1, rotate: 0, opacity: 1 }}
                    transition={{ type: "spring", damping: 15, stiffness: 200 }}
                    className="relative z-10"
                  >
                    {/* Pulsing ring effect */}
                    <motion.div
                      animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                      className="absolute inset-0 w-20 h-20 bg-white/20 rounded-2xl"
                    />
                    <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white shadow-2xl border border-white/30 relative">
                      <motion.div
                        animate={{ y: [0, -2, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        className="w-10 h-10"
                      >
                        {onboardingSteps[currentStep].icon}
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Step Indicator Dots */}
                  <div className="flex items-center gap-1.5 mt-6">
                    {onboardingSteps.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentStep(index)}
                        className={`transition-all duration-300 ${
                          index === currentStep
                            ? 'w-6 h-2 bg-white rounded-full'
                            : index < currentStep
                            ? 'w-2 h-2 bg-white/60 rounded-full hover:bg-white/80'
                            : 'w-2 h-2 bg-white/30 rounded-full hover:bg-white/50'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Progress Bar */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                    <motion.div
                      className="h-full bg-white"
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentStep + 1) / onboardingSteps.length) * 100}%` }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    />
                  </div>
                </div>

                {/* Right Side - Content */}
                <div className="md:w-[60%] p-6 flex flex-col relative">
                  {/* Close Button */}
                  <button
                    onClick={handleCompleteOnboarding}
                    className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-400 hover:text-neutral-600 transition-all hover:rotate-90 duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {/* Welcome Badge - Only on first step */}
                  {currentStep === 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-primary-50 to-blue-50 text-primary-600 rounded-full text-xs font-semibold mb-4 w-fit border border-primary-100"
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
                      </span>
                      Welcome to Aktivity
                    </motion.div>
                  )}

                  {/* Content */}
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="flex-1"
                  >
                    <h3 className="text-xl font-bold text-neutral-900 mb-2 leading-tight">
                      {onboardingSteps[currentStep].title}
                    </h3>
                    <p className="text-neutral-500 leading-relaxed mb-4 text-sm">
                      {onboardingSteps[currentStep].description}
                    </p>

                    {/* Feature highlights with staggered animation */}
                    <div className="space-y-2 mb-4">
                      {currentStep === 0 && (
                        <>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            Invite trainers via email
                          </motion.div>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            Assign trainers to sessions
                          </motion.div>
                        </>
                      )}
                      {currentStep === 1 && (
                        <>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            Set dates, times & participant limits
                          </motion.div>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            Define session objectives
                          </motion.div>
                        </>
                      )}
                      {currentStep === 2 && (
                        <>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            Real-time engagement tracking
                          </motion.div>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            View live activity responses
                          </motion.div>
                        </>
                      )}
                      {currentStep === 3 && (
                        <>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            Detailed session reports
                          </motion.div>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            Export data for analysis
                          </motion.div>
                        </>
                      )}
                    </div>
                  </motion.div>

                  {/* Navigation */}
                  <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
                    <button
                      onClick={handlePrevStep}
                      disabled={currentStep === 0}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        currentStep === 0
                          ? 'text-neutral-300 cursor-not-allowed'
                          : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back
                    </button>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleCompleteOnboarding}
                        className="px-3 py-2 text-sm font-medium text-neutral-400 hover:text-neutral-600 transition-colors"
                      >
                        Skip
                      </button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleNextStep}
                        className={`flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all shadow-md ${
                          currentStep === 0 ? 'bg-gradient-to-r from-purple-500 to-violet-600 shadow-purple-500/20' :
                          currentStep === 1 ? 'bg-gradient-to-r from-blue-500 to-indigo-600 shadow-blue-500/20' :
                          currentStep === 2 ? 'bg-gradient-to-r from-emerald-500 to-teal-600 shadow-emerald-500/20' :
                          'bg-gradient-to-r from-rose-500 to-pink-600 shadow-rose-500/20'
                        }`}
                      >
                        {currentStep === onboardingSteps.length - 1 ? (
                          <>
                            Get Started
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          </>
                        ) : (
                          <>
                            Continue
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                            </svg>
                          </>
                        )}
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <div className="p-4 space-y-4 pb-20 sm:pb-4">
        {/* Welcome Header */}
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-secondary-900">
              Welcome back, {user?.firstName || user?.fullName?.split(' ')[0] || 'User'}!
            </h1>
            <p className="text-xs sm:text-sm text-neutral-600 mt-1">Here's what's happening with your training sessions today.</p>
          </div>
          <button
            type="button"
            onClick={handleReplayTour}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-neutral-600 ring-1 ring-inset ring-neutral-200 hover:bg-neutral-50 hover:text-neutral-900 transition-colors flex-shrink-0"
            title="Replay the welcome tour"
          >
            <Compass className="w-4 h-4" />
            <span className="hidden sm:inline">Show me around</span>
          </button>
        </div>

        {/* Inactive Trainers Warning */}
        {inactiveTrainerCount > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 sm:p-4 mb-4">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-orange-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <p className="text-xs sm:text-sm text-orange-800">
                  <span className="font-semibold">{inactiveTrainerCount} trainer{inactiveTrainerCount > 1 ? 's are' : ' is'} inactive</span> and cannot be assigned to new sessions.{' '}
                  <button onClick={() => navigate('/client/trainers')} className="underline hover:no-underline font-medium">
                    Manage trainers
                  </button>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Getting Started Checklist */}
        {!loading && !gsDismissed && (
          <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-base font-bold text-neutral-900">
                  {gsAllDone ? "🎉 You're all set!" : 'Get started with Aktivity'}
                </h2>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {gsAllDone
                    ? "You've completed the key setup steps."
                    : `Complete these steps to set up your training · ${gsCompleted} of ${gettingStartedSteps.length} done`}
                </p>
              </div>
              <button
                type="button"
                onClick={dismissGettingStarted}
                className="text-xs font-medium text-neutral-400 hover:text-neutral-700 transition-colors flex-shrink-0"
              >
                Dismiss
              </button>
            </div>

            {/* Progress bar */}
            <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden mb-4">
              <div
                className="h-full bg-primary-500 transition-all duration-500"
                style={{ width: `${(gsCompleted / gettingStartedSteps.length) * 100}%` }}
              />
            </div>

            {/* Steps */}
            <ul className="space-y-2">
              {gettingStartedSteps.map((step) => (
                <li
                  key={step.key}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 ${
                    step.done ? 'bg-neutral-50' : 'bg-white ring-1 ring-inset ring-neutral-200/70'
                  }`}
                >
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      step.done ? 'bg-green-500 text-white' : 'border-2 border-neutral-300'
                    }`}
                  >
                    {step.done && (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <span
                    className={`flex-1 text-sm ${
                      step.done ? 'text-neutral-400 line-through' : 'text-neutral-700 font-medium'
                    }`}
                  >
                    {step.label}
                  </span>
                  {!step.done && (
                    <button
                      type="button"
                      onClick={step.action}
                      className="text-xs font-semibold text-primary-600 hover:text-primary-700 px-3 py-1.5 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap"
                    >
                      {step.cta} →
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsCards.map((stat) => (
            <StatCard key={stat.title} label={stat.title} value={stat.value} icon={stat.icon} variant={stat.variant} />
          ))}
        </div>

        {/* Recent Activity Timeline */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-3 sm:p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-base font-bold text-neutral-900">Recent Activity</h2>
              <p className="text-xs text-neutral-500">Latest session updates</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/client/sessions')}>
              View All
            </Button>
          </div>

          {sessions.length > 0 ? (
            <div className="relative">
              {sessions.slice(0, 6).map((session, index) => {
                const isLast = index === Math.min(sessions.length, 6) - 1;
                const statusConfig = {
                  live: { color: 'bg-emerald-100 text-emerald-600', icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z', label: 'Session is live' },
                  scheduled: { color: 'bg-blue-100 text-blue-600', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', label: 'Session scheduled' },
                  completed: { color: 'bg-purple-100 text-purple-600', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', label: 'Session completed' },
                  expired: { color: 'bg-red-100 text-red-600', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', label: 'Session expired' },
                };
                const config = statusConfig[session.status] || statusConfig.scheduled;
                return (
                  <div
                    key={session.id}
                    className="relative flex gap-4 pb-6 last:pb-0 cursor-pointer group"
                    onClick={() => navigate(`/client/sessions/${session.id}`)}
                  >
                    {!isLast && (
                      <div className="absolute left-[17px] top-10 bottom-0 w-px bg-neutral-200" />
                    )}
                    <div className={`relative z-10 w-9 h-9 rounded-full ${config.color} flex items-center justify-center flex-shrink-0`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={config.icon} />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <p className="text-xs sm:text-sm text-neutral-900 font-medium group-hover:text-primary-600 transition-colors">{session.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-neutral-400">
                          {session.startTime ? formatDateWithTimezone(session.startTime) : 'Not scheduled'}
                        </span>
                        <span className="text-neutral-300">&middot;</span>
                        <span className="text-xs font-medium text-neutral-500">{config.label}</span>
                      </div>
                      {session.trainerName && (
                        <div className="mt-2 px-3 py-2 bg-neutral-50 rounded-lg border border-neutral-100">
                          <p className="text-xs text-neutral-600">Trainer: {session.trainerName} &middot; {session.currentParticipants || 0}/{session.expectedParticipants || session.maxParticipants} participants</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-neutral-400 text-xs sm:text-sm">No recent activity</div>
          )}
        </div>

        {/* Upcoming Sessions */}
        <div className="bg-white rounded-2xl border border-neutral-100 overflow-hidden">
          <div className="p-3 sm:p-5 border-b border-neutral-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-neutral-900">Upcoming Sessions</h2>
                <p className="text-xs text-neutral-500">Sessions scheduled for the next 7 days</p>
              </div>
              <Button variant="primary" size="sm" onClick={() => navigate('/client/sessions/new')}>
                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                New Session
              </Button>
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-neutral-100">
                  <th className="text-left py-3.5 px-5 text-xs sm:text-sm font-medium text-neutral-500">Session</th>
                  <th className="text-left py-3.5 px-5 text-xs sm:text-sm font-medium text-neutral-500">Trainer</th>
                  <th className="text-left py-3.5 px-5 text-xs sm:text-sm font-medium text-neutral-500">Date & Time</th>
                  <th className="text-left py-3.5 px-5 text-xs sm:text-sm font-medium text-neutral-500">Participants</th>
                  <th className="text-left py-3.5 px-5 text-xs sm:text-sm font-medium text-neutral-500">Status</th>
                  <th className="text-right py-3.5 px-5 text-xs sm:text-sm font-medium text-neutral-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions
                  .filter((s) => s.status === 'scheduled')
                  .map((session) => (
                    <tr
                      key={session.id}
                      className="border-b border-neutral-50 hover:bg-neutral-50/60 transition-colors cursor-pointer"
                      onClick={() => navigate(`/client/sessions/${session.id}`)}
                    >
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center text-primary-600 font-semibold text-sm flex-shrink-0">
                            {session.name.charAt(0)}
                          </div>
                          <div>
                            <p className="text-xs sm:text-sm font-medium text-neutral-900">{session.name}</p>
                            <p className="text-xs text-neutral-400">Code: {session.code}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <span className="text-xs sm:text-sm text-neutral-700">{session.trainerName}</span>
                      </td>
                      <td className="py-4 px-5">
                        <p className="text-xs sm:text-sm text-neutral-900">
                          {formatDateWithTimezone(session.startTime)}
                        </p>
                      </td>
                      <td className="py-4 px-5">
                        <span className="text-xs sm:text-sm text-neutral-700">
                          {session.currentParticipants}/{session.expectedParticipants || session.maxParticipants}
                        </span>
                      </td>
                      <td className="py-4 px-5">
                        <Badge variant="scheduled" size="sm">{session.status}</Badge>
                      </td>
                      <td className="py-4 px-5 text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => navigate(`/client/sessions/${session.id}`)}
                          className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
                          title="View"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden">
            {sessions
              .filter((s) => s.status === 'scheduled')
              .map((session) => (
                <div
                  key={session.id}
                  className="p-4 border-b border-neutral-100 active:bg-neutral-50 cursor-pointer"
                  onClick={() => navigate(`/client/sessions/${session.id}`)}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 bg-primary-50 rounded-full flex items-center justify-center text-primary-600 font-semibold text-sm flex-shrink-0">
                      {session.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-semibold text-neutral-900 truncate">{session.name}</p>
                      <p className="text-xs text-neutral-400">Code: {session.code}</p>
                    </div>
                    <Badge variant="scheduled" size="sm">{session.status}</Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-neutral-500 ml-12">
                    <span>{session.trainerName}</span>
                    <span className="text-neutral-300">&middot;</span>
                    <span>{formatDateWithTimezone(session.startTime)}</span>
                    <span className="text-neutral-300">&middot;</span>
                    <span>{session.currentParticipants}/{session.expectedParticipants || session.maxParticipants}</span>
                  </div>
                </div>
              ))}
          </div>

          {sessions.filter((s) => s.status === 'scheduled').length === 0 && (
            <div className="text-center py-12 md:py-16 px-4">
              <div className="inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 bg-neutral-100 rounded-full mb-3">
                <svg className="w-5 h-5 md:w-6 md:h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-xs sm:text-sm text-neutral-600 font-medium mb-1">No upcoming sessions</p>
              <p className="text-xs text-neutral-400 mb-4">Schedule a new training session to get started</p>
              <Button variant="primary" size="sm" onClick={() => navigate('/client/sessions/new')}>
                Schedule New Session
              </Button>
            </div>
          )}
        </div>

        {/* Quick Actions - Single Row */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-4">
          <div className="mb-3">
            <h2 className="text-base font-bold text-neutral-900">Quick Actions</h2>
            <p className="text-xs text-neutral-500">Common tasks</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {quickActions.map((action) => {
              // Map the legacy variant names ('purple', 'green') to the
              // StatCard palette so the quick-action chips read identically
              // to stat card icons across the app.
              const variantMap = { blue: 'blue', purple: 'violet', green: 'emerald' };
              const chipVariant = variantMap[action.variant] || 'blue';
              return (
                <button
                  key={action.title}
                  type="button"
                  onClick={action.action}
                  className="group rounded-2xl bg-white ring-1 ring-inset ring-neutral-200/70 p-4 text-left hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-4px_rgba(16,24,40,0.10)] hover:ring-neutral-300 transition-all duration-200"
                >
                  <div className="flex items-center gap-3 sm:flex-col sm:items-start">
                    <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform ${STAT_CHIP[chipVariant]}`}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                        {action.icon.props.children}
                      </svg>
                    </span>
                    <div className="sm:mt-3">
                      <h3 className="font-semibold text-neutral-900 text-sm">{action.title}</h3>
                      <p className="text-xs text-neutral-500 mt-0.5">{action.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ClientDashboard;
